/**
 * IM 会话总控 hook：UI 与 IImGateway 之间的唯一桥梁。
 *
 * 职责：连接生命周期、会话列表、历史消息、收发（文本/图/视频/文件）、
 * 好友申请箱、好友扩展（备注/删除/拉黑）、建群/搜群/加群/退群、已读标记。
 * 全部数据面都走 gateway（OpenIM WASM SDK）；Nexora 只在 start() 时换 bootstrap。
 *
 * 连接状态机（ImConnectionState）：
 *
 *   ┌──────┐  start()   ┌────────────┐  login 成功   ┌───────┐
 *   │ idle │ ─────────► │ connecting │ ────────────► │ ready │
 *   └──────┘            └─────┬──────┘               └───┬───┘
 *     ▲                       │ login 失败/连接中断        │
 *     │                       ▼                          │ stop()
 *     │                   ┌───────┐                        │
 *     │                   │ error │                        │
 *     │                   └───────┘                        │
 *     └───────────────────────────────────────────────────┘
 *       （error 状态下错误文本进 error state；stop() 回 idle）
 *
 * 数据流：gateway 事件（onState/onConversationsChanged/onNewMessages/
 * onFriendApplicationChanged）→ 本 hook 的 useState → 各面板 props。
 * 写路径（send 系、accept 系、create 系）成功后手动刷新对应 state，
 * 不依赖 SDK 推送时序（角标、列表才跟得上手）。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchImBootstrap } from "../auth/bootstrap";
import { clearImBootstrap, saveImBootstrap } from "../auth/imToken";
import {
  captureVideoSnapshot,
  getImGateway,
} from "../gateway/openimSdkGateway";
import type { ImConnectionState } from "../ports";
import type { FriendApplication, ImConversation, ImMessage, SelfProfile } from "../types";

export function useImSession() {
  const gateway = useMemo(() => getImGateway(), []);
  const [state, setState] = useState<ImConnectionState>("idle");
  const [error, setError] = useState("");
  const [conversations, setConversations] = useState<ImConversation[]>([]);
  const [activeId, setActiveId] = useState("");
  const [messages, setMessages] = useState<ImMessage[]>([]);
  const [selfId, setSelfId] = useState("");
  /** 收到的好友申请（含已处理；UI 可只展示 handleResult===0） */
  const [friendApps, setFriendApps] = useState<FriendApplication[]>([]);
  // 当前用户openim资料
  const [selfProfile, setSelfProfile] = useState<SelfProfile | null>(null);
  /**
   * 用 ref 保存最新 activeId，供 onNewMessages 回调读取，
   * 避免闭包拿到旧值；hooks 只能在组件顶层调用，不能写进回调里。
   */
  const activeIdRef = useRef(activeId);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const refreshFriendApps = useCallback(async () => {
    try {
      const list = await gateway.getFriendApplicationsAsRecipient();
      setFriendApps(list);
    } catch {
      /* 未 ready 时忽略 */
    }
  }, [gateway]);

  useEffect(() => {
    const offState = gateway.onState((s, err) => {
      setState(s);
      if (err) setError(err);
    });
    const offConv = gateway.onConversationsChanged(setConversations);
    const offMsg = gateway.onNewMessages((incoming) => {
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => m.clientMsgID));
        const merged = [...prev];
        for (const m of incoming) {
          if (!ids.has(m.clientMsgID)) merged.push(m);
        }
        return merged;
      });
      void gateway.getConversations().then(setConversations).catch(() => undefined);

      // 正停留在某会话时再 mark 一次，避免角标又亮起
      const cur = activeIdRef.current;
      if (cur) {
        void gateway.markConversationAsRead(cur).catch(() => undefined);
      }
    });
    const offFriend = gateway.onFriendApplicationChanged(() => {
      void refreshFriendApps();
    });
    return () => {
      offState();
      offConv();
      offMsg();
      offFriend();
    };
  }, [gateway, refreshFriendApps]);

  /**
   * 建立连接：状态机 idle → connecting → ready/error。
   * 步骤：① Nexora 换 bootstrap ② 本地缓存 ③ SDK login
   *      ④ 拉会话列表/本人资料/好友申请箱（资料失败不阻断）。
   */
  const start = useCallback(async () => {
    setError("");
    const boot = await fetchImBootstrap();
    saveImBootstrap(boot);
    setSelfId(boot.im_user_id);
    await gateway.connect(boot);
    setConversations(await gateway.getConversations());
    try {
      setSelfProfile(await gateway.getSelfProfile())
    } catch {
      // 获取资料失败时 不阻断
    }
    await refreshFriendApps();
  }, [gateway, refreshFriendApps]);

  /**
   * 打开会话：切 UI → 拉历史 → 标记已读（清未读角标）。
   */
  const openConversation = useCallback(
    async (conversationID: string) => {
      setActiveId(conversationID);
      setMessages(await gateway.getHistory(conversationID));
      try {
        await gateway.markConversationAsRead(conversationID);
        // 乐观更新：不等 OnConversationChanged，本地先把角标清 0
        setConversations((prev) =>
          prev.map((c) =>
            c.conversationID === conversationID
              ? { ...c, unreadCount: 0 }
              : c,
          ),
        );
      } catch (err) {
        // 已读失败不阻断看消息；角标可能暂时还在
        console.warn("markConversationAsRead failed:", err);
      }
    },
    [gateway],
  );
  /**
   * 发送文本消息, 返回消息对象, 用于在消息列表里展示, 方便点击后复制/转发/引用等
   */
  const sendText = useCallback(
    async (text: string) => {
      const conv = conversations.find((c) => c.conversationID === activeId);
      if (!conv) throw new Error("未选择会话");
      const isGroup = Boolean(conv.groupID);
      const msg = await gateway.sendText({
        text,
        recvID: isGroup ? conv.groupID! : conv.userID!,
        groupID: conv.groupID,
        isGroup,
      });
      setMessages((prev) => [...prev, msg]);
    },
    [activeId, conversations, gateway],
  );
  // 从当前会话解析recvID / isGroup 供图片/文件/视频共用
  const resolveTarget = useCallback(()=>{
    const conv = conversations.find((c) => c.conversationID === activeId); // 当前会话
    if (!conv) throw new Error("未选择会话");
    const isGroup = Boolean(conv.groupID);
    return {
      recvID: isGroup ? conv.groupID! : conv.userID!,
      groupID: conv.groupID,
      isGroup: Boolean(conv.groupID),
    };
  },[activeId, conversations])
 /**
  * 发送图片消息, 返回消息对象, 用于在消息列表里展示, 方便点击后查看/转发/引用等
  */
  const sendImage = useCallback(
    async (file: File)=>{
      const t = resolveTarget();
      const msg = await gateway.sendImage({ file,...t});
      setMessages((prev) => [...prev, msg]); // 乐观更新
    },
    [gateway, resolveTarget],
  );
  
  const sendFile = useCallback(
    async (file: File) => {
      const t = resolveTarget(); 
      const msg = await gateway.sendFile({ file,...t});
      setMessages((prev) => [...prev, msg]); // 乐观更新
    },
    [gateway, resolveTarget],
  )

  const sendVideo = useCallback(
    async (file: File) => {
      // 先截封面，再交给 gateway（OpenIM 强制要 snapshot）
      const snap = await captureVideoSnapshot(file);
      const t = resolveTarget();
      const msg = await gateway.sendVideo({
        videoFile: file,
        snapshotFile: snap.snapshotFile,
        durationSec: snap.durationSec,
        snapshotWidth: snap.snapshotWidth,
        snapshotHeight: snap.snapshotHeight,
        ...t,
      });
      setMessages((prev) => [...prev, msg]);
    },
    [gateway, resolveTarget],
  );

  /** 加好友：传入目录里的 openim_user_id，不要传裸 username */
  const addFriend = useCallback(
    async (openImUserId: string, reqMsg?: string) => {
      await gateway.addFriend(openImUserId, reqMsg);
    },
    [gateway],
  );

  const acceptFriend = useCallback(
    async (fromUserID: string) => {
      await gateway.acceptFriendApplication(fromUserID);
      await refreshFriendApps();
      setConversations(await gateway.getConversations());
    },
    [gateway, refreshFriendApps],
  );

  const refuseFriend = useCallback(
    async (fromUserID: string) => {
      await gateway.refuseFriendApplication(fromUserID);
      await refreshFriendApps();
    },
    [gateway, refreshFriendApps],
  );

  const createGroup = useCallback(
    async (groupName: string, memberUserIDs: string[]) => {
      const gid = await gateway.createGroup({ groupName, memberUserIDs });
      setConversations(await gateway.getConversations());
      return gid;
    },
    [gateway],
  );

  /**
   * 断开并复位：SDK logout → 清本地 bootstrap 缓存 → 清空全部 UI state。
   * 退出登录/切账号时必须走这里，避免串号或旧会话残留。
   */
  const stop = useCallback(async () => {
    await gateway.disconnect();
    clearImBootstrap();
    setConversations([]);
    setMessages([]);
    setActiveId("");
    setFriendApps([]);
    setSelfProfile(null); // 清空资料
  }, [gateway]);

  /** 只改昵称（弹窗点保存时调用） */
  const updateNickname = useCallback(
    async (nickname: string) => {
      // 调网关写 OpenIM
      await gateway.updateSelfProfile({ nickname });
      // 再读一遍，保证和服务器一致
      setSelfProfile(await gateway.getSelfProfile());
    },
    [gateway],
  );
  // 选图-> 上传 -> 写faceurl -> 更新资料
  const updateAvatar = useCallback(
    async (file: File) => {
      // 1) 上传拿 URL
      const url = await gateway.uploadAvatar(file);
      // 2) 写入个人资料
      await gateway.updateSelfProfile({ faceURL: url });
      // 3) 刷新 UI 状态
      setSelfProfile(await gateway.getSelfProfile());
    },
    [gateway],
  );

  const getFriendProfile = useCallback(
    (useID: string)=>gateway.getFriendProfile(useID),
    [gateway],
  )

  const deleteFriend = useCallback(
    async (userID: string)=>{
      await gateway.deleteFriend(userID);
      // 删完刷新会话（showName / 列表可能变）
      setConversations(await gateway.getConversations());
    },
    [gateway],
  )

  const addBlack = useCallback(
    async (userID: string)=>{
      await gateway.addBlack(userID);
      // 拉黑后会话列表可能变
      setConversations(await gateway.getConversations());
    },
    [gateway],
  )

  const setFriendRemark = useCallback(
    async (userID: string, remark: string) => {
      await gateway.setFriendRemark(userID, remark);
      // 备注常会反映到会话 showName
      setConversations(await gateway.getConversations());
    },
    [gateway],
  );
  const getJoinedGroups = useCallback(
    () => gateway.getJoinedGroups(),
    [gateway],
  );
  const inviteToGroup = useCallback(
    async (groupID: string, userID: string) => {
      await gateway.inviteToGroup({
        groupID,
        userIDList: [userID],
        reason: "邀请入群",
      });
    },
    [gateway],
  );

  /** 建群弹窗：加载可选好友（OpenIM getFriendList，不是 Nexora 目录） */
  const listFriends = useCallback(() => gateway.listFriends(), [gateway]);

  /** 加群面板：按关键字搜群 */
  const searchGroups = useCallback(
    (keyword: string) => gateway.searchGroups(keyword),
    [gateway],
  );

  /**
   * 申请加群。成功后刷新会话——
   * 无需审批时新群会出现在列表；需审批时列表可能暂时不变。
   */
  const joinGroup = useCallback(
    async (groupID: string, reqMsg?: string) => {
      await gateway.joinGroup({ groupID, reqMsg });
      setConversations(await gateway.getConversations());
    },
    [gateway],
  );

  /** 退群后刷新会话列表 */
  const quitGroup = useCallback(
    async (groupID: string) => {
      await gateway.quitGroup(groupID);
      setConversations(await gateway.getConversations());
    },
    [gateway],
  );

  return {
    state,
    error,
    selfId,
    conversations,
    activeId,
    messages,
    friendApps,
    start,
    stop,
    openConversation,
    sendText,
    addFriend,
    acceptFriend,
    refuseFriend,
    createGroup,
    refreshFriendApps,
    selfProfile,
    updateNickname,
    updateAvatar,
    sendImage,
    sendFile,
    sendVideo,
    getFriendProfile,
    deleteFriend,
    addBlack,
    setFriendRemark,
    getJoinedGroups,
    inviteToGroup,
    listFriends,
    searchGroups,
    joinGroup,
    quitGroup,
  };
}
